import React, { useEffect, useMemo, useRef, useState } from "react";
import { Routes, Route, NavLink, Link, useParams } from "react-router-dom";
import api, { fmtCLP, resolveImg } from "../lib/api";
import ProcessTimeline from "../components/ProcessTimeline";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import {
  LayoutDashboard, Briefcase, Users, MessageSquare, BookKey, MailQuestion, BarChart3, Tags,
  CheckCircle, XCircle, Star, Eye, Pause, Play, Pencil, PlusCircle, Trash2, ChevronLeft, Search, Send, FileLock2, Unlock, Lock,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";
import { toast } from "sonner";
import StatusBadge from "../components/StatusBadge";

import { NewBusiness, EditBusiness } from "./SellerPanel";

const NAV = [
  { to: "/admin", icon: <LayoutDashboard className="w-4 h-4" />, label: "Dashboard", end: true, testId: "dashboard" },
  { to: "/admin/businesses", icon: <Briefcase className="w-4 h-4" />, label: "Negocios", section: "businesses" },
  { to: "/admin/new", icon: <PlusCircle className="w-4 h-4" />, label: "Publicar negocio" },
  { to: "/admin/requests", icon: <MailQuestion className="w-4 h-4" />, label: "Solicitudes", section: "requests" },
  { to: "/admin/documents", icon: <FileLock2 className="w-4 h-4" />, label: "Acceso documentos", section: "documents" },
  { to: "/admin/users", icon: <Users className="w-4 h-4" />, label: "Usuarios" },
  { to: "/admin/maintainers", icon: <Tags className="w-4 h-4" />, label: "Mantenedores" },
];

export default function AdminPanel() {
  return (
    <DashboardLayout title="Súper Admin" subtitle="Panel administrativo" items={NAV} testId="admin-panel">
      <Routes>
        <Route index element={<Dashboard />} />
        <Route path="businesses" element={<Businesses />} />
        <Route path="new" element={<NewBusiness />} />
        <Route path="edit/:id" element={<EditBusiness />} />
        <Route path="requests" element={<Requests />} />
        <Route path="documents" element={<AdminDocAccess />} />
        <Route path="conversations/:id" element={<AdminConversation />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="maintainers" element={<Maintainers />} />
      </Routes>
    </DashboardLayout>
  );
}

const COLORS = ["#059669", "#D97706", "#0F172A", "#71717A", "#EF4444", "#3B82F6"];

function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setData(r.data)); }, []);
  if (!data) return <p>Cargando...</p>;

  const cards = [
    { label: "Negocios publicados", value: data.total_businesses, color: "bg-slate-900" },
    { label: "Activos", value: data.active, color: "bg-emerald-600" },
    { label: "Pendientes", value: data.pending, color: "bg-amber-600" },
    { label: "Vendidos", value: data.sold, color: "bg-slate-700" },
    { label: "Vendedores", value: data.sellers, color: "bg-slate-700" },
    { label: "Inversionistas", value: data.investors, color: "bg-slate-700" },
    { label: "Solicitudes", value: data.requests, color: "bg-slate-700" },
    { label: "Destacados", value: data.featured, color: "bg-amber-500" },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard-content">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-zinc-200 rounded-lg p-5">
            <p className="text-xs overline text-slate-500">{c.label}</p>
            <p className="font-display text-3xl font-extrabold mt-2">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-zinc-200 rounded-lg p-6">
          <p className="overline text-emerald-700">Comisión estimada potencial</p>
          <p className="font-display text-3xl font-extrabold mt-2 text-emerald-700">{fmtCLP(data.potential_commission)}</p>
          <p className="text-xs text-slate-500 mt-1">Sobre negocios activos al 6%</p>
        </div>
        <div className="bg-slate-900 text-white rounded-lg p-6">
          <p className="overline text-amber-400">Comisión real cerrada</p>
          <p className="font-display text-3xl font-extrabold mt-2 text-amber-400">{fmtCLP(data.realized_commission)}</p>
          <p className="text-xs text-slate-400 mt-1">Sobre negocios vendidos</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-sm">Ranking por categoría</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.category_ranking}>
                <XAxis dataKey="name" fontSize={10} angle={-25} textAnchor="end" height={60} />
                <YAxis fontSize={10} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#059669" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-sm">Ranking por región</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={data.region_ranking} dataKey="count" nameKey="name" outerRadius={80} label>
                  {data.region_ranking.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-sm">Últimas solicitudes</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Negocio</TableHead><TableHead>Inversionista</TableHead><TableHead>Estado</TableHead><TableHead>Fecha</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.latest_requests.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.business_title}</TableCell>
                  <TableCell>
                  <div className="leading-tight">
                    <p className="text-sm font-medium text-slate-900">{r.investor_name || r.investor_alias}</p>
                    {r.investor_email && <p className="text-xs text-slate-500">{r.investor_email}</p>}
                  </div>
                </TableCell>
                  <TableCell><StatusBadge status={r.status} type="request" /></TableCell>
                  <TableCell className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString("es-CL", { timeZone: "America/Santiago" })}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function Businesses() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const load = () => api.get(`/admin/businesses${filter ? `?status=${filter}` : ""}`).then((r) => setList(r.data));
  useEffect(() => { load(); }, [filter]);

  const setStatus = async (biz, status, reason = null) => {
    await api.patch(`/admin/businesses/${biz.id}/status`, { status, rejection_reason: reason });
    toast.success("Estado actualizado");
    load();
  };
  const toggleFeatured = async (biz) => {
    await api.patch(`/admin/businesses/${biz.id}/featured?featured=${!biz.is_featured}`);
    load();
  };

  const del = async (b) => {
    if (!window.confirm(`¿Eliminar el negocio "${b.title}"?\nEsta acción borrará el anuncio, sus imágenes, documentos y todas las solicitudes asociadas. No se puede deshacer.`)) return;
    try {
      await api.delete(`/seller/businesses/${b.id}`);
      toast.success("Negocio eliminado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-businesses">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {[
            ["", "Todos"],
            ["pending", "Pendientes"],
            ["approved", "Aprobados"],
            ["rejected", "Rechazados"],
            ["paused", "Pausados"],
            ["sold", "Vendidos"],
          ].map(([s, label]) => (
            <Button key={s || "all"} size="sm" variant={filter === s ? "default" : "outline"} onClick={() => setFilter(s)} data-testid={`filter-status-${s || "all"}`}>
              {label}
            </Button>
          ))}
        </div>
        <Button asChild size="sm" className="btn-investment" data-testid="admin-new-biz">
          <Link to="/admin/new"><PlusCircle className="w-4 h-4 mr-1.5" /> Publicar negocio</Link>
        </Button>
      </div>
      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Negocio</TableHead><TableHead>Categoría</TableHead><TableHead>Precio</TableHead><TableHead>Estado</TableHead><TableHead>Comisión</TableHead><TableHead>Acciones</TableHead></TableRow></TableHeader>
          <TableBody>
            {list.map((b) => (
              <TableRow key={b.id}>
                <TableCell><div className="font-medium">{b.title}</div><div className="text-xs text-slate-500">{b.region_name}, {b.comuna_name}</div></TableCell>
                <TableCell>{b.category_name}</TableCell>
                <TableCell>{fmtCLP(b.sale_price)}</TableCell>
                <TableCell><StatusBadge status={b.status} /></TableCell>
                <TableCell>{b.commission_percentage}%</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {b.status === "pending" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setStatus(b, "approved")} data-testid={`approve-${b.id}`}><CheckCircle className="w-4 h-4 text-emerald-600" /></Button>
                        <Button size="sm" variant="outline" onClick={() => setStatus(b, "rejected", "Revisión inicial")} data-testid={`reject-${b.id}`}><XCircle className="w-4 h-4 text-rose-600" /></Button>
                      </>
                    )}
                    {b.status === "approved" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => toggleFeatured(b)} data-testid={`feature-${b.id}`} title="Destacar"><Star className={`w-4 h-4 ${b.is_featured ? "fill-amber-500 text-amber-500" : ""}`} /></Button>
                        <Button size="sm" variant="outline" onClick={() => setStatus(b, "paused")} title="Pausar"><Pause className="w-4 h-4" /></Button>
                        <SellDialog biz={b} onDone={load} />
                      </>
                    )}
                    {b.status === "paused" && (
                      <Button size="sm" variant="outline" onClick={() => setStatus(b, "approved")} data-testid={`resume-${b.id}`} title="Quitar pausa" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                        <Play className="w-4 h-4" /> <span className="ml-1 text-xs">Quitar pausa</span>
                      </Button>
                    )}
                    {b.status === "rejected" && (
                      <Button size="sm" variant="outline" onClick={() => setStatus(b, "pending")} title="Re-enviar a revisión">
                        Re-enviar
                      </Button>
                    )}
                    <CommissionDialog biz={b} onDone={load} />
                    <Button asChild size="sm" variant="outline" data-testid={`edit-${b.id}`} title="Editar"><Link to={`/admin/edit/${b.id}`}><Pencil className="w-4 h-4" /></Link></Button>
                    <Button asChild size="sm" variant="ghost" title="Ver"><a href={`/business/${b.slug}`} target="_blank" rel="noreferrer"><Eye className="w-4 h-4" /></a></Button>
                    <Button size="sm" variant="ghost" onClick={() => del(b)} data-testid={`delete-${b.id}`} title="Eliminar"><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function DocumentReleaseDialog() { return null; }

function AdminDocAccess() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [notesById, setNotesById] = useState({});
  const load = () => api.get("/admin/document-access").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const decide = async (id, status) => {
    try {
      await api.patch(`/admin/document-access/${id}`, { status, decision_notes: notesById[id] || null });
      toast.success(status === "approved" ? "Acceso aprobado al inversionista" : status === "rejected" ? "Solicitud rechazada" : "Acceso revocado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const filtered = filter === "all" ? list : list.filter((r) => r.status === filter);
  const fmtDateShort = (iso) => iso ? new Date(iso).toLocaleString("es-CL", { timeZone: "America/Santiago", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
  const STATUS = {
    pending:  { cls: "bg-amber-100 text-amber-800 border-amber-200", text: "Pendiente" },
    approved: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", text: "Aprobado" },
    rejected: { cls: "bg-rose-100 text-rose-800 border-rose-200", text: "Rechazado" },
    revoked:  { cls: "bg-slate-100 text-slate-700 border-slate-200", text: "Revocado" },
  };

  return (
    <div className="space-y-4" data-testid="admin-doc-access">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-display text-lg font-bold flex items-center gap-2"><FileLock2 className="w-5 h-5" /> Autorización de acceso a documentos</h2>
        <div className="ml-auto flex gap-1">
          {[["all", "Todas"], ["pending", "Pendientes"], ["approved", "Aprobadas"], ["rejected", "Rechazadas"]].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition ${filter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-zinc-200 hover:bg-zinc-50"}`}
              data-testid={`adm-doc-filter-${k}`}
            >{l}</button>
          ))}
        </div>
      </div>
      <p className="text-xs text-slate-500">
        El administrador es quien autoriza el acceso de un inversionista a los documentos privados de cada negocio. Los documentos sólo quedan disponibles dentro de la sesión del inversionista aprobado.
      </p>
      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Negocio</TableHead>
            <TableHead>Inversionista</TableHead>
            <TableHead>Justificación</TableHead>
            <TableHead>Fecha</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead>Acciones</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">Sin solicitudes.</TableCell></TableRow>}
            {filtered.map((r) => {
              const s = STATUS[r.status] || STATUS.pending;
              return (
                <TableRow key={r.id} data-testid={`adm-doc-row-${r.id}`}>
                  <TableCell className="font-medium">{r.business_title}</TableCell>
                  <TableCell>
                    <div className="leading-tight">
                      <p className="text-sm font-medium text-slate-900">{r.investor_name || r.investor_alias}</p>
                      {r.investor_email && <p className="text-xs text-slate-500">{r.investor_email}</p>}
                      {r.investor_phone && <p className="text-xs text-slate-500">{r.investor_phone}</p>}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm max-w-xs">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Justificación del inversionista</p>
                    <p className="line-clamp-3 text-slate-700 mt-0.5">{r.justification || <span className="text-slate-400 italic">El inversionista no agregó comentarios.</span>}</p>
                    {r.status === "pending" && (
                      <div className="mt-2">
                        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Tu nota al inversionista (opcional)</label>
                        <Input
                          placeholder="Ej. Documentos liberados, contáctanos por dudas."
                          value={notesById[r.id] || ""}
                          onChange={(e) => setNotesById({ ...notesById, [r.id]: e.target.value })}
                          className="mt-1 h-8 text-xs"
                        />
                      </div>
                    )}
                    {r.decision_notes && <p className="mt-2 text-xs text-slate-500 italic">Tu nota: {r.decision_notes}</p>}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{fmtDateShort(r.created_at)}</TableCell>
                  <TableCell><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${s.cls}`}>{s.text}</span></TableCell>
                  <TableCell className="space-x-2 whitespace-nowrap">
                    {r.status === "pending" && (
                      <>
                        <Button size="sm" className="btn-investment" onClick={() => decide(r.id, "approved")} data-testid={`adm-approve-${r.id}`}>Aprobar</Button>
                        <Button size="sm" variant="outline" onClick={() => decide(r.id, "rejected")} className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid={`adm-reject-${r.id}`}>Rechazar</Button>
                      </>
                    )}
                    {r.status === "approved" && (
                      <Button size="sm" variant="outline" onClick={() => decide(r.id, "revoked")} className="text-slate-700" data-testid={`adm-revoke-${r.id}`}>Revocar</Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function CommissionDialog({ biz, onDone }) {
  const [open, setOpen] = useState(false);
  const [pct, setPct] = useState(biz.commission_percentage);
  const save = async () => {
    if (pct < 5 || pct > 8) { toast.error("Comisión entre 5% y 8%"); return; }
    await api.patch(`/admin/businesses/${biz.id}/commission`, { commission_percentage: pct });
    toast.success("Comisión actualizada");
    onDone();
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`commission-${biz.id}`}>{biz.commission_percentage}%</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Comisión (5%-8%)</DialogTitle></DialogHeader>
        <Label>Porcentaje</Label>
        <Input type="number" min={5} max={8} step={0.1} value={pct} onChange={(e) => setPct(parseFloat(e.target.value))} data-testid={`commission-input-${biz.id}`} />
        <DialogFooter><Button onClick={save} data-testid={`commission-save-${biz.id}`}>Guardar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SellDialog({ biz, onDone }) {
  const [open, setOpen] = useState(false);
  const [price, setPrice] = useState(parseFloat(biz.sale_price));
  const save = async () => {
    await api.patch(`/admin/businesses/${biz.id}/sold`, { sold_price: price, commission_paid: false });
    toast.success("Negocio marcado como vendido");
    onDone(); setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`mark-sold-${biz.id}`}>Vender</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Marcar como vendido</DialogTitle></DialogHeader>
        <Label>Precio final</Label>
        <Input type="number" value={price} onChange={(e) => setPrice(parseFloat(e.target.value))} data-testid={`sold-price-${biz.id}`} />
        <p className="text-xs text-slate-500">Comisión {biz.commission_percentage}%: {fmtCLP(price * biz.commission_percentage / 100)}</p>
        <DialogFooter><Button onClick={save} data-testid={`sold-save-${biz.id}`}>Confirmar venta</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Requests() {
  const [list, setList] = useState([]);
  const load = () => api.get("/requests/mine").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (r, status) => {
    await api.patch(`/admin/requests/${r.id}`, { status });
    toast.success("Estado actualizado");
    load();
  };
  const KIND = {
    info:  { label: "Información", cls: "bg-slate-100 text-slate-700 border-slate-200" },
    offer: { label: "Oferta",      cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    visit: { label: "Visita",      cls: "bg-amber-100 text-amber-800 border-amber-200" },
    meeting:{ label: "Reunión",    cls: "bg-violet-100 text-violet-800 border-violet-200" },
  };
  const fmtDateShort = (iso) => iso ? new Date(iso).toLocaleString("es-CL", { timeZone: "America/Santiago", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

  // Group requests by (business_id, investor_id) so each thread appears as one row
  const groups = React.useMemo(() => {
    const map = new Map();
    list.forEach((r) => {
      const k = `${r.business_id}::${r.investor_id}`;
      if (!map.has(k)) {
        map.set(k, {
          key: k,
          business_id: r.business_id,
          business_title: r.business_title,
          investor_id: r.investor_id,
          investor_name: r.investor_name,
          investor_email: r.investor_email,
          investor_alias: r.investor_alias,
          items: [],
        });
      }
      map.get(k).items.push(r);
    });
    // canonical thread id = earliest request; last_activity = max created_at
    return Array.from(map.values()).map((g) => {
      const items = g.items.slice().sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      const lastActivity = items.reduce((acc, x) => new Date(x.created_at) > new Date(acc) ? x.created_at : acc, items[0].created_at);
      const hasUnresolved = items.some((x) => ["pending", "in_review", "in_conversation"].includes(x.status));
      return { ...g, items, thread_id: items[0].id, last_activity: lastActivity, hasUnresolved };
    }).sort((a, b) => new Date(b.last_activity) - new Date(a.last_activity));
  }, [list]);

  return (
    <div className="space-y-3" data-testid="admin-requests">
      <p className="text-xs text-slate-500">
        Las solicitudes se agrupan por <strong>negocio + inversionista</strong>. Cada fila representa una conversación única con todas las gestiones (información, oferta, visita) del mismo interesado sobre el mismo negocio.
      </p>
      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Negocio</TableHead>
            <TableHead>Inversionista</TableHead>
            <TableHead>Gestiones</TableHead>
            <TableHead>Última actividad</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead>Acciones</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {groups.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Sin solicitudes.</TableCell></TableRow>}
            {groups.map((g) => {
              const offer = g.items.find((x) => x.kind === "offer");
              const visit = g.items.find((x) => x.kind === "visit");
              const latest = g.items[g.items.length - 1];
              return (
                <TableRow key={g.key} data-testid={`adm-req-group-${g.business_id}-${g.investor_id}`}>
                  <TableCell className="font-medium align-top py-4">
                    <p>{g.business_title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{g.items.length} gestión{g.items.length === 1 ? "" : "es"} · hilo único</p>
                  </TableCell>
                  <TableCell className="align-top py-4">
                    <div className="leading-tight">
                      <p className="text-sm font-medium text-slate-900">{g.investor_name || g.investor_alias}</p>
                      {g.investor_email && <p className="text-xs text-slate-500">{g.investor_email}</p>}
                    </div>
                  </TableCell>
                  <TableCell className="align-top py-4">
                    <div className="flex flex-wrap gap-1.5">
                      {g.items.map((it) => {
                        const k = KIND[it.kind || "info"];
                        return (
                          <span
                            key={it.id}
                            title={`${k.label} · ${fmtDateShort(it.created_at)}`}
                            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${k.cls}`}
                          >{k.label}</span>
                        );
                      })}
                    </div>
                    {(offer || visit) && (
                      <div className="mt-2 text-xs text-slate-600 space-y-0.5">
                        {offer && <p>Oferta: <strong className="text-emerald-700">{fmtCLP(offer.offer_amount)}</strong></p>}
                        {visit && <p>Visita: <strong>{fmtDateShort(visit.visit_date)}</strong></p>}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 align-top py-4">{fmtDateShort(g.last_activity)}</TableCell>
                  <TableCell className="align-top py-4">
                    <StatusBadge status={g.hasUnresolved ? "in_conversation" : latest.status} type="request" />
                  </TableCell>
                  <TableCell className="align-top py-4">
                    <div className="flex flex-wrap gap-1">
                      <Button asChild size="sm" variant="outline" data-testid={`admin-open-conv-${g.thread_id}`}>
                        <Link to={`/admin/conversations/${g.thread_id}`}>Conversación</Link>
                      </Button>
                      {g.hasUnresolved && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => g.items.filter((x) => x.status === "pending").forEach((x) => setStatus(x, "in_conversation"))} data-testid={`approve-group-${g.key}`}>Aprobar</Button>
                          <Button size="sm" variant="outline" onClick={() => g.items.forEach((x) => setStatus(x, "closed"))}>Cerrar</Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function AdminConversation() {
  const { id } = useParams();
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [req, setReq] = useState(null);
  const [query, setQuery] = useState("");
  const bottomRef = useRef(null);

  const load = () => {
    api.get(`/requests/${id}`).then((r) => setReq(r.data));
    api.get(`/messages/${id}`).then((r) => setMsgs(r.data));
  };
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [msgs.length]);

  const filtered = useMemo(() => {
    if (!query.trim()) return msgs;
    const q = query.toLowerCase();
    return msgs.filter((m) => (m.body || "").toLowerCase().includes(q));
  }, [msgs, query]);

  const send = async () => {
    if (!body.trim()) return;
    try {
      await api.post("/messages", { request_id: id, body });
      toast.success("Mensaje enviado");
      setBody("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="max-w-[1600px] space-y-4" data-testid="admin-conversation-page">
      <Link to="/admin/requests" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 hover:underline" data-testid="admin-back-to-requests">
        <ChevronLeft className="w-4 h-4" /> Volver a solicitudes
      </Link>
      <div className="bg-white border border-zinc-200 rounded-lg p-5 flex items-start gap-4">
        {req?.business_image && (
          <Link to={req?.business_slug ? `/business/${req.business_slug}` : "#"} className="shrink-0">
            <img src={resolveImg(req.business_image, 200)} alt={req.business_title} className="w-20 h-20 rounded-lg object-cover border border-zinc-200" />
          </Link>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Hilo unificado · Inversionista ↔ Equipo</p>
          <h2 className="font-display text-xl font-bold truncate">{req?.business_title}</h2>
          <p className="text-xs text-slate-500 mt-2">Inversionista: <strong>{req?.investor_name || req?.investor_alias}</strong>{req?.investor_email && <span className="ml-2 text-slate-400">· {req.investor_email}</span>}{req?.investor_phone && <span className="ml-2 text-slate-400">· {req.investor_phone}</span>}</p>
          <div className="flex items-center gap-2 mt-2"><span className="text-xs text-slate-500">Estado:</span> {req && <StatusBadge status={req.status} type="request" />}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
        <div className="space-y-4 min-w-0">
          <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar en la conversación…"
          className="w-full pl-9 pr-9 py-2 text-sm bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          data-testid="admin-conv-search"
        />
        {query && (
          <button type="button" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-sm">✕</button>
        )}
      </div>

      <div className="rounded-lg p-5 space-y-2 max-h-[560px] overflow-y-auto border border-zinc-200" style={{ background: "#efeae2" }}>
        {filtered.length === 0 && (
          <p className="text-sm text-slate-600 text-center py-6">{query ? "Sin coincidencias." : "Aún no hay mensajes."}</p>
        )}
        {filtered.map((m) => {
          if (m.sender_role === "system") {
            return (
              <div key={m.id} className="flex justify-center">
                <div className="text-[11px] text-slate-700 bg-white/80 border border-zinc-200 rounded-md px-3 py-1 shadow-sm">{m.body}</div>
              </div>
            );
          }
          const mine = m.sender_role === "super_admin";
          const label = mine ? "Equipo (tú)" : "Inversionista";
          const time = m.created_at ? new Date(m.created_at).toLocaleTimeString("es-CL", { timeZone: "America/Santiago", hour: "2-digit", minute: "2-digit" }) : "";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[72%] px-3 py-2 rounded-lg text-sm shadow-sm ${mine ? "rounded-tr-none" : "rounded-tl-none border border-zinc-200"}`}
                style={mine ? { background: "#d9fdd3" } : { background: "#ffffff" }}
              >
                <span className="text-[11px] font-semibold mr-1 text-emerald-800">{label}:</span>
                <span className="whitespace-pre-wrap break-words text-slate-900">{m.body}</span>
                {time && <span className="text-[10px] text-slate-500 ml-2 align-baseline">{time}</span>}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-4 space-y-2">
        <Textarea rows={3} placeholder="Responder al inversionista…" value={body} onChange={(e) => setBody(e.target.value)} data-testid="admin-conv-input" />
        <div className="flex justify-end items-center">
          <Button onClick={send} className="btn-primary" data-testid="admin-send-msg-btn"><Send className="w-4 h-4 mr-2" /> Enviar</Button>
        </div>
      </div>
        </div>

        <aside className="lg:sticky lg:top-24 self-start w-full">
          {req?.business_id && <ProcessTimeline businessId={req.business_id} investorId={req.investor_id} />}
        </aside>
      </div>
    </div>
  );
}

function Messages() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/messages/pending").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const moderate = async (m, status, body = null) => {
    await api.patch(`/admin/messages/${m.id}`, { status, body });
    toast.success("Mensaje moderado");
    load();
  };
  if (list.length === 0) return (
    <div className="bg-white border border-zinc-200 rounded-lg p-8 text-center" data-testid="admin-messages-empty">
      <p className="text-slate-700 font-medium">No hay mensajes pendientes de revisión.</p>
      <p className="text-xs text-slate-500 mt-2">El chat es directo entre el inversionista y la administración: los mensajes se aprueban automáticamente. Esta vista sólo lista mensajes históricos retenidos antes del cambio de política.</p>
    </div>
  );
  return (
    <div className="space-y-3" data-testid="admin-messages">
      {list.map((m) => (
        <div key={m.id} className="bg-white border border-zinc-200 rounded-lg p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">{m.business_title}</p>
              <p className="text-sm font-semibold">De: {m.sender_alias}</p>
            </div>
            <Badge variant="destructive">{m.flagged_reasons}</Badge>
          </div>
          <p className="mt-3 p-3 bg-zinc-50 rounded text-sm">{m.body}</p>
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={() => moderate(m, "approved")} data-testid={`approve-msg-${m.id}`}>Aprobar</Button>
            <Button size="sm" variant="outline" onClick={() => moderate(m, "edited", m.body.replace(/\d/g, "*"))}>Editar y aprobar</Button>
            <Button size="sm" variant="destructive" onClick={() => moderate(m, "blocked")} data-testid={`block-msg-${m.id}`}>Bloquear</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function UsersPage() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/users").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const toggle = async (u) => {
    await api.patch(`/admin/users/${u.id}/active?is_active=${!u.is_active}`);
    load();
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto" data-testid="admin-users">
      <Table>
        <TableHeader><TableRow><TableHead>Nombre</TableHead><TableHead>Email</TableHead><TableHead>Rol</TableHead><TableHead>Estado</TableHead><TableHead>Acción</TableHead></TableRow></TableHeader>
        <TableBody>
          {list.map((u) => (
            <TableRow key={u.id}>
              <TableCell className="font-medium">{u.full_name}</TableCell>
              <TableCell>{u.email}</TableCell>
              <TableCell><StatusBadge status={u.role} type="role" /></TableCell>
              <TableCell>{u.is_active ? <StatusBadge status="approved" /> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 border border-rose-200">Inactivo</span>}</TableCell>
              <TableCell><Button size="sm" variant="outline" onClick={() => toggle(u)}>{u.is_active ? "Desactivar" : "Activar"}</Button></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function Maintainers() {
  const [tab, setTab] = useState("regions");
  return (
    <div data-testid="admin-maintainers">
      <div className="flex gap-2 mb-6 flex-wrap">
        {[
          ["regions", "Regiones"], ["categories", "Categorías"], ["faqs", "FAQs"], ["testimonials", "Testimonios"]
        ].map(([k, l]) => (
          <Button key={k} size="sm" variant={tab === k ? "default" : "outline"} onClick={() => setTab(k)} data-testid={`maintainer-tab-${k}`}>{l}</Button>
        ))}
      </div>
      {tab === "regions" && <RegionsMaint />}
      {tab === "categories" && <CategoriesMaint />}
      {tab === "faqs" && <FaqsMaint />}
      {tab === "testimonials" && <p className="text-slate-500 text-sm">Edición de testimonios disponible en próxima iteración.</p>}
    </div>
  );
}

function RegionsMaint() {
  const [list, setList] = useState([]);
  const load = () => api.get("/regions").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4">
      <p className="text-sm text-slate-500 mb-3">{list.length} regiones registradas (precargadas en seed)</p>
      <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {list.map((r) => (<li key={r.id} className="text-sm p-2 border border-zinc-100 rounded">{r.code} · {r.name}</li>))}
      </ul>
    </div>
  );
}

function CategoriesMaint() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", slug: "" });
  const load = () => api.get("/categories").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.name || !form.slug) return;
    await api.post("/admin/categories", form);
    setForm({ name: "", slug: "" });
    load();
    toast.success("Categoría creada");
  };
  const del = async (id) => { await api.delete(`/admin/categories/${id}`); load(); };
  return (
    <div className="space-y-4">
      <div className="bg-white border border-zinc-200 rounded-lg p-4 flex gap-2 flex-wrap">
        <Input placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="max-w-xs" data-testid="new-category-name" />
        <Input placeholder="Slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="max-w-xs" data-testid="new-category-slug" />
        <Button onClick={add} data-testid="add-category-btn">Agregar</Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map((c) => (
          <div key={c.id} className="bg-white border border-zinc-200 rounded-lg p-3 flex items-center justify-between">
            <span className="text-sm font-medium">{c.name}</span>
            <Button size="icon" variant="ghost" onClick={() => del(c.id)} data-testid={`del-cat-${c.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function FaqsMaint() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ question: "", answer: "" });
  const load = () => api.get("/faqs").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.question || !form.answer) return;
    await api.post("/admin/faqs", form);
    setForm({ question: "", answer: "" });
    load();
    toast.success("FAQ creada");
  };
  const del = async (id) => { await api.delete(`/admin/faqs/${id}`); load(); };
  return (
    <div className="space-y-4">
      <div className="bg-white border border-zinc-200 rounded-lg p-4 space-y-2">
        <Input placeholder="Pregunta" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="new-faq-q" />
        <Textarea placeholder="Respuesta" value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} rows={3} data-testid="new-faq-a" />
        <Button onClick={add} data-testid="add-faq-btn">Agregar FAQ</Button>
      </div>
      <div className="space-y-2">
        {list.map((f) => (
          <div key={f.id} className="bg-white border border-zinc-200 rounded-lg p-4 flex justify-between gap-3">
            <div>
              <p className="text-sm font-semibold">{f.question}</p>
              <p className="text-xs text-slate-500 mt-1">{f.answer}</p>
            </div>
            <Button size="icon" variant="ghost" onClick={() => del(f.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}
